import { State, Action, StateContext, Selector, Select, Store, actionMatcher, SelectorOptions, createSelector } from '@ngxs/store';
import { StoryChaptersStateModel } from './story.chapters.state.model';
import { 
  SetChapters,
  SelectChapter,
  NextChapter,
  PreviousChapter,
  LastChapter,
  FirstChapter,
  RemoveChapter, 
  ResetStoryChapters,
  RegisterChapterThumbnails,
  TakeChapterPicture,
  TakeChapterPictureSuccess,
  DuplicateChapter,
  CopyThumbnail,
  SetAllChaptersBackground,
  SetSelectedChapterBackground,
  ResetSelectedChapterBackground,
  SetChapterBackground,
  SelectChapterById,
  RecordCamera,
  SetChapterClippingPlane,
  ApplyChapterPatch,
  SetChapterCameraAction,
  SetChapterUI,
  SetChapterInfo,
  ReorderChapters,
  SetChapterReadyState,
  SetChapterPostProcessing,
  SetChapterTransitions,
  SetChapterTransparencyMode,
  SetChapterLegend,
  AddToChapterLegend,
  SetChapterCurrentLayersIndexPosition,
  SetChapterMedia,
  SetChapterContentBlocks
} from './story.chapters.actions';

import { ChapterService } from '../../chapter.service';
import { RemoveAllChapterLabels, CopyLabels } from '../labels/story.labels.actions';
import { classToClass } from 'class-transformer';
import { SnapshotService } from 'src/app/engine/snapshot.service';
import { SetVisible } from 'src/app/engine/state/engine.actions';
import { StoryChapterThumbnail } from '../../models/story-chapter-thumbnail.model';
import { get, cloneDeep } from 'lodash';
import { ChapterModel } from '../../models/chapter.model';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { appDefaultBackground } from 'src/app/dialogs/components/background-color-dialog/background-config';
import { ThumbnailConstants } from 'src/app/shared/thumbnail.constants';
import { SnapshotConfig } from 'src/app/engine/models/snapshot-config.model';
import { AnimationService } from 'src/app/engine/animation.service';
import { MediaModel } from '../../models/media.model';
import { ColorUtils } from 'src/app/shared/libraries/colors/colors';

import { EngineService } from 'src/app/engine/engine.service';
import { UndoActions } from 'src/app/undo/state/undo.state.actions';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { NodeActions } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { Injectable } from '@angular/core';
import { VisibilityLayerModel } from '../../models/visibility-layers.model';
import { ContentBlock } from 'src/app/content-blocks/models/content-block';
import { QuizActions } from 'src/app/quiz/state/quiz.actions';
import { ImageTypeEnum } from 'src/app/shared/models/image-type.enum';
import { ContentBlockTypes } from 'src/app/content-blocks/models/content-block-types';

declare const immer;


// snapshot config
const defaultSnapshotConfig: SnapshotConfig = {
  imageType: ImageTypeEnum.JPEG,
  crop: 'original',

  copyrightEnabled: false,
  copyrightImagePath: 'assets/images/powered-by-biodigital-color.svg',
  copyrightPosition: {
      x: 'left',
      y: 'bottom'
  },
  copyrightOffset: {
      x: 12,
      y: -12
  },
  imageQuality: 1.0
};

const initialState: StoryChaptersStateModel = {
  loading: false,
  chapters: [],
  chaptersViewed: [],
  selectedChapter: null,
  isChapterAnimated: false,
  thumbnails: new Map(),
  patches: [],
  inversePatches: []
};

@State<StoryChaptersStateModel>({
  name: 'storyChapters',
  defaults: initialState
})
@Injectable()
export class StoryChaptersState {
  /**
   * Selectors
   */
  @Selector()
  static getStoryChapters(state: StoryChaptersStateModel) {
    return state.chapters;
  }

  @Selector()
  static getChaptersViewed(state: StoryChaptersStateModel) {
    return state.chaptersViewed;
  }

  static getChapter(chapterId: string) {
    return createSelector([StoryChaptersState], (state: StoryChaptersStateModel) => {
      return state.chapters.find(chapter => chapter.id === chapterId);
    });
  }

  @Selector()
  static getSelectedChapterId(state: StoryChaptersStateModel) {
    return state.selectedChapter;
  }

  @Selector()
  static getSelectedChapter(state: StoryChaptersStateModel) {
    return state.chapters.find(chapter => chapter.id === state.selectedChapter) || null;
  }

  @Selector()
  static getNextChapter(state: StoryChaptersStateModel) {
    const index = 1 + StoryChaptersState.getSelectedChapterIndex(state);
    return state.chapters[index];
  }

  @Selector()
  static getPreviousChapter(state: StoryChaptersStateModel) {
    const index = -1 + StoryChaptersState.getSelectedChapterIndex(state);
    return state.chapters[index];
  }

  @Selector()
  static getSelectedChapterIndex(state: StoryChaptersStateModel) {
    return state.chapters.findIndex(chapter => chapter.id === state.selectedChapter);
  }

  @Selector()
  static getSelectedChapterTitle(state: StoryChaptersStateModel) {
    return state.chapters.find(chapter => chapter.id === state.selectedChapter).info.displayName;
  }

  @Selector()
  static getChapterCount(state: StoryChaptersStateModel) {
    return state.chapters.length;
  }

  @Selector()
  static getRegisteredThumbnails(state: StoryChaptersStateModel) {
    return state.thumbnails;
  }

  @Selector()
  static getIsChapterAnimated(state: StoryChaptersStateModel) {
    return state.isChapterAnimated;
  }

  @Selector()
  static getSelectedChapterReadyState(state: StoryChaptersStateModel) {
    const { chapters, selectedChapter } = state;
    const chapter = StoryChaptersState.findChapter(chapters, selectedChapter);
    return get(chapter, 'readyState');
  }

  @Selector()
  static getChaptersLoaded(state: StoryChaptersStateModel) {
    return {
      count: state.chapters.length,
      modelChapters: state.chapters.filter(chapter => chapter.readyState.model),
      animationChapters: state.chapters.filter(chapter => chapter.readyState.animation)
    };
  }

  @Selector()
  static getAllChaptersModelsReady(state: StoryChaptersStateModel) {
    return state.chapters.length > 0 && state.chapters.every(chapter => chapter.readyState.model);
  }

  @Selector()
  static getAllChaptersAnimationsReady(state: StoryChaptersStateModel) {
    return state.chapters.length > 0 && state.chapters.every(chapter => chapter.readyState.animation);
  }

  @Selector()
  static getSelectedChapterBackground(state: StoryChaptersStateModel) {
    const { chapters, selectedChapter } = state;
    const chapter = StoryChaptersState.findChapter(chapters, selectedChapter);
    return get(chapter, 'background') || appDefaultBackground;
  }

  @Selector()
  static getAllChaptersBackground(state: StoryChaptersStateModel) {
    return state.chapters
      .map((chapter) => get(chapter, 'background'));
  }

  @Selector()
  static getSelectedChapterAudio(state: StoryChaptersStateModel) {
    const selectedChapter = state.chapters.find(chapter => chapter.id === state.selectedChapter);    
    if (selectedChapter && selectedChapter.media) {
      const media: { [key: string]: MediaModel } = selectedChapter.media;
      const audioClips = Object.values(media)
        .filter(({ data }) => data.streamType === 'audio');
      return audioClips[0];
    } else {
      return null;
    }
  }

  @Selector()
  static getSelectedChapterLegend(state: StoryChaptersStateModel) {
    const { chapters, selectedChapter } = state;
    const chapter = StoryChaptersState.findChapter(chapters, selectedChapter);
    return get(chapter, 'legend') || [];
  }

  @Selector()
  static getSelectedChapterClippingPlanes(state: StoryChaptersStateModel) {
    const { chapters, selectedChapter } = state;
    const chapter = StoryChaptersState.findChapter(chapters, selectedChapter);
    return get(chapter, 'clippingPlanes');
  }

  @Selector()
  static getChapterCamera(state: StoryChaptersStateModel) {
    const { chapters, selectedChapter } = state;
    const chapter = StoryChaptersState.findChapter(chapters, selectedChapter);
    return get(chapter, 'camera.location');
  }

  static findChapter(chapters: ChapterModel[], chapterId: string): ChapterModel {
    return chapters
      .find((chapter) => chapter.id === chapterId);
  }

  @Selector()
  static getAllChaptersCurrentLayers(state: StoryChaptersStateModel): Array<VisibilityLayerModel>[] {
    return state.chapters
      .map((chapter) => get(chapter, 'visibilityLayers.current.layers', []));
  }

  @Selector()
  static getSelectedChapterSrcLayers(state: StoryChaptersStateModel): VisibilityLayerModel[] {
    const { chapters, selectedChapter } = state;
    const chapter = StoryChaptersState.findChapter(chapters, selectedChapter);
    return get(chapter, 'visibilityLayers.src.layers', []);
  }
  
  @Selector()
  static getSelectedChapterCurrentLayers(state: StoryChaptersStateModel): VisibilityLayerModel[] {
    const { chapters, selectedChapter } = state;
    const chapter = StoryChaptersState.findChapter(chapters, selectedChapter);
    return get(chapter, 'visibilityLayers.current.layers', []);
  }

  @Selector()
  static getSelectedChapterCurrentLayersIndex(state: StoryChaptersStateModel): number {
    const { chapters, selectedChapter } = state;
    const chapter = StoryChaptersState.findChapter(chapters, selectedChapter);
    return get(chapter, 'visibilityLayers.current.index');
  }

  @Selector()
  static getSelectedChapterCurrentLayersPosition(state: StoryChaptersStateModel): number {
    const { chapters, selectedChapter } = state;
    const chapter = StoryChaptersState.findChapter(chapters, selectedChapter);
    return get(chapter, 'visibilityLayers.current.position');
  }

  @Selector()
  static getSelectedChapterContentBlocks(state: StoryChaptersStateModel): ContentBlock[] {
    const { chapters, selectedChapter } = state;
    const chapter = StoryChaptersState.findChapter(chapters, selectedChapter);
    return get(chapter, 'contentBlocks', []);
  }
  

  /**
   * Actions
   */
  @Action(SetChapters)
  setChapters(context: StateContext<StoryChaptersStateModel>, action: SetChapters) {
    context.patchState(immer.produce(context.getState(), draft => {
      draft.chapters = action.chapters;
    }));
  }


  @Action(SetChapterCameraAction)
  SetChapterCameraAction(context: StateContext<StoryChaptersStateModel>, action: SetChapterCameraAction) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        chapter.camera.action = action.cameraAction;
      })
    );
  }

  @Action(SetChapterUI)
  setChapterUI(context: StateContext<StoryChaptersStateModel>, action: SetChapterUI) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        chapter.ui = action.ui;
      })
    );
  }

  @Action(SetChapterInfo)
  setChapterInfo(context: StateContext<StoryChaptersStateModel>, action: SetChapterInfo) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        chapter.info = action.info;
      })
    );
  }

  @Action(SetChapterLegend)
  setChapterLegend(context: StateContext<StoryChaptersStateModel>, action: SetChapterLegend) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        chapter.legend = action.legend;
      })
    );
  }

  @Action(AddToChapterLegend)
  addToChapterLegend(context: StateContext<StoryChaptersStateModel>, action: AddToChapterLegend) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        chapter.legend.push(action.legend);
      })
    );
  }

  @Action(ReorderChapters)
  ReorderChapters(context: StateContext<StoryChaptersStateModel>, action: ReorderChapters) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        moveItemInArray(draft.chapters, action.previousIndex, action.currentIndex);
      })
    );

    // reorder quiz questions
    const chapterIds = context.getState()
      .chapters
      .map((chapter) => chapter.id);

    context.dispatch(new QuizActions.ReorderQuestions(chapterIds));
  }


  @Action(SetChapterReadyState)
  SetChapterReadyState(context: StateContext<StoryChaptersStateModel>, action: SetChapterReadyState) {
    const nextState = immer.produce(context.getState(), draft => {
      if (action.chapterId) {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        if (chapter) {
          chapter.readyState = {...chapter.readyState, ...action.readyState};
        }
      }
    });
    context.patchState(nextState);
  }

  @Action(SetChapterPostProcessing)
  SetChapterPostProcessing(context: StateContext<StoryChaptersStateModel>, action: SetChapterPostProcessing) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        const customData = chapter.customData ? chapter.customData : {};
        const properties = chapter.properties || {};
        chapter.customData = {...customData, postprocess: action.effects };
        chapter.properties = {...properties, ...action.properties};
      })
    );
  }

  @Action(SetChapterTransitions)
  SetChapterTransitions(context: StateContext<StoryChaptersStateModel>, action: SetChapterTransitions) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        chapter.transitions = chapter.transitions || {};
        chapter.transitions[action.key] = action.transition;
      })
    );
  }

  @Action(SetChapterTransparencyMode) 
  SetChapterTransparencyMode(context: StateContext<StoryChaptersStateModel>, action: SetChapterTransparencyMode) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        chapter.transparencyMode = action.transparencyMode;
      })
    );
  }

  @Action(ApplyChapterPatch)
  applyChapterPatch(context: StateContext<StoryChaptersStateModel>, action: ApplyChapterPatch) {
    const patched = immer.applyPatches(context.getState(), action.patches);
    context.patchState(
      immer.produce(patched, draft => {
        draft.patches = [];
        draft.inversePatches = [];
      })
    );
  }

  @Action(SetChapterClippingPlane)
  setChapterClippingPlane(context: StateContext<StoryChaptersStateModel>, action: SetChapterClippingPlane) {
    const [nextState, patches, inversePatches] = immer.produceWithPatches(context.getState(), draft => {
      const chapter = draft.chapters.find(c => c.id === action.chapterId);
      chapter.clippingPlanes.clips = action.clips;
      chapter.clippingPlanes.enabled = action.enabled;
      chapter.clippingPlanes.boundary =
        chapter.clippingPlanes.boundary ||
        JSON.parse(JSON.stringify(this.engineService.getSceneBoundary()));
    });

    const finalState = immer.produce(nextState, draft => {
      draft.patches.push(...patches);
      draft.inversePatches.push(...inversePatches);
    });

    if (action.record) {
      context.dispatch(
        new UndoActions.SavePatches([{
          forward: finalState.patches,
          inverse: [...finalState.inversePatches].reverse(),
          patchAction: ApplyChapterPatch
        }])
      );
    }

    context.patchState(
      action.record
        ? immer.produce(finalState, draft => {
            draft.patches = [];
            draft.inversePatches = [];
          })
        : finalState
    );
  }

  @Action(SetChapterBackground)
  setChapterBackground(context: StateContext<StoryChaptersStateModel>, action: SetChapterBackground) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        chapter.background = action.background;
      })
    );
  }

  @Action(SetAllChaptersBackground)
  setAllChaptersBackground(context: StateContext<StoryChaptersStateModel>, action: SetAllChaptersBackground) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        draft.chapters.forEach(chapter => {
          chapter.background = action.background;
        });
      })
    );
  }

  @Action(ResetSelectedChapterBackground)
  resetSelectedChapterBackground(context: StateContext<StoryChaptersStateModel>, action: ResetSelectedChapterBackground) {
    const { selectedChapter } = context.getState();
    this.store.dispatch(new SetChapterBackground(selectedChapter, appDefaultBackground));
  }

  @Action(SetSelectedChapterBackground)
  setSelectedChapterBackground(context: StateContext<StoryChaptersStateModel>, { background }) {
    const chapterId = context.getState().selectedChapter;
    this.store.dispatch(new SetChapterBackground(chapterId, background));
  }

  @Action(SelectChapter)
  selectChapter(context: StateContext<StoryChaptersStateModel>, action: SelectChapter) {
    if (!action.chapter) {
      return;
    }

    this.store.dispatch([
      new NodeActions.UpdateNodesChecked(false),
      new NodeActions.UpdateNodeSelected()
    ]);

    const isAnimated = this.animationService.isChapterAnimated(action.chapter);
    this.store.dispatch(new SetVisible(action.chapter ? true : false));

    const nextState = immer.produce(context.getState(), draft => {
      if (!draft.chaptersViewed.some(chapterId => action.chapter.id === chapterId)) {
        draft.chaptersViewed.push(action.chapter.id);
      }
      draft.selectedChapter = action.chapter ? action.chapter.id : null;
      draft.isChapterAnimated = isAnimated;
    });
    context.patchState(nextState);
  }

  @Action(SelectChapterById)
  selectChapterById(context: StateContext<StoryChaptersStateModel>, {chapterId}) {
    this.store.dispatch(new SetVisible(true));
    context.patchState({
      selectedChapter: chapterId
    });
  }


  @Action(DuplicateChapter)
  duplicateChapter(context: StateContext<StoryChaptersStateModel>, action: DuplicateChapter) {
    // create a safe chapter copy
    const sourceChapter = action.chapter;
    const targetChapter = this.chapterService.copyChapter(sourceChapter, action.chapterId);
    
    // insert chapter copy into ENGINE
    const chapters = context.getState().chapters;
    const insertIndex = chapters.length;
    
    // #NOTE: [EM] deep copy is important. Otherwise engine replaces timeline map object.
    this.chapterService.addChapterToEngine(cloneDeep(targetChapter), insertIndex); 

    // update application STATE
    context.patchState(immer.produce(context.getState(), draft => {
      draft.chapters.push(targetChapter);
    }));

    context.dispatch(new QuizActions.CopyQuestions(sourceChapter.id, targetChapter.id));

    context.dispatch(new CopyLabels(sourceChapter, targetChapter));
    context.dispatch(new CopyThumbnail(sourceChapter, targetChapter));
  }

  @Action(CopyThumbnail)
  copyThumbnail(context: StateContext<StoryChaptersStateModel>, action: CopyThumbnail) {
    // copy thumbnail or take new snapshot for target chapter
    
    if (action.sourceChapter) {
      const thumbnails = context.getState().thumbnails;
      const nextState = immer.produce(context.getState(), draft => {
        // copy, and point snapshot to original large url
        const sourceThumbnail = thumbnails.get(action.sourceChapter.id);
        const targetThumbnail = new StoryChapterThumbnail();
        targetThumbnail.storyId = sourceThumbnail.storyId;
        targetThumbnail.chapterId = sourceThumbnail.chapterId;
        targetThumbnail.urls = {
          small: sourceThumbnail.urls.small.replace(
            action.sourceChapter.id,
            action.targetChapter.id
          ),
          large: sourceThumbnail.urls.large.replace(
            action.sourceChapter.id,
            action.targetChapter.id
          )
        };
        targetThumbnail.snapshot = sourceThumbnail.snapshot
          ? sourceThumbnail.snapshot
          : sourceThumbnail.urls.large;
      
        draft.thumbnails.set(action.targetChapter.id, targetThumbnail);
      });
      
      context.patchState(nextState);
      
    } else {
      // take a snapshot
      context.dispatch(new TakeChapterPicture(action.targetChapter.id, ImageTypeEnum.JPEG, 92, ThumbnailConstants.medium, 'square'));
    }
  }

  @Action(NextChapter)
  nextChapter(context: StateContext<StoryChaptersStateModel>, action: NextChapter) {
    const chapters = context.getState().chapters;
    const currentChapterId = this.chapterService.getCurrentChapterId();
    let index = 0;
    index = chapters.findIndex(chapter => chapter.id === currentChapterId);
    if (index < chapters.length - 1) {
      index++;
    }
    
    context.dispatch(new SelectChapter(chapters[index]));
  }

  @Action(PreviousChapter)
  previousChapter(context: StateContext<StoryChaptersStateModel>, action: PreviousChapter) {
    const chapters = context.getState().chapters;
    const currentChapterId = this.chapterService.getCurrentChapterId();
    let index = 0;
    index = chapters.findIndex(chapter => chapter.id === currentChapterId);
    if (index > 0) {
      index--;
    }

    context.dispatch(new SelectChapter(chapters[index]));    
  }

  @Action(LastChapter)
  lastChapter(context: StateContext<StoryChaptersStateModel>, action: LastChapter) {
    const chapters = context.getState().chapters;
    
    context.dispatch(new SelectChapter(chapters[chapters.length - 1]));
  }

  @Action(FirstChapter)
  firstChapter(context: StateContext<StoryChaptersStateModel>, action: LastChapter) {
    const chapters = context.getState().chapters;
    
    context.dispatch(new SelectChapter(chapters[0]));
  }
    
  @Action(RemoveChapter)
  removeChapter(context: StateContext<StoryChaptersStateModel>, action: RemoveChapter) {
    const { chapters, selectedChapter } = context.getState();

    // Don't allow the user to remove the last chapter,
    // or else the system will break. 
    if (chapters.length === 1) {
      return;
    }

    // Trigger a rebuild of the chapter object in the engine before updating the application. 
    this.chapterService.removeChapterFromEngine(action.chapter);

    // Update chapters array
    const chapterIndex = chapters.findIndex(chapter => chapter.id === action.chapter.id);
    
    context.patchState(immer.produce(context.getState(), draft => {
      draft.chapters.splice(chapterIndex, 1);
    }));

    // Selected chapter deleted, need to select a new chapter by default
    if (action.chapter.id === selectedChapter) {
      // get updated chapters
      const patchedChapters = context.getState().chapters;
      // Make sure selected chapter doesn't exceed chapter array size
      const targetChapterIndex = Math.min(chapterIndex, patchedChapters.length - 1);
      const targetChapter = patchedChapters[targetChapterIndex];
      
      context.dispatch(new SelectChapter(targetChapter));
    }

    // Nuke all chapter labels
    context.dispatch(new RemoveAllChapterLabels(action.chapter));
  }
  
  @Action(ResetStoryChapters)
  resetStoryChaptersState(context: StateContext<StoryChaptersStateModel>, action: ResetStoryChapters) {
    context.setState(initialState);
  }


  @Action(RegisterChapterThumbnails)
  registerChapterThumbnails(context: StateContext<StoryChaptersStateModel>, action: RegisterChapterThumbnails) {
    // urls that came with scene for managing and housekeeping of server thumbnails per chapter
    context.patchState(immer.produce(context.getState(), draft => {
      draft.thumbnails = new Map(action.thumbnails);
    }));
  }

  @Action(TakeChapterPicture)
  takeChapterPicture(context: StateContext<StoryChaptersStateModel>, action: TakeChapterPicture) {
    const { chapterId, includeLabels, includeCopyright, dimensions, imageType, imageQuality, crop, layers } = action;
    // get target dimensions: fallback to default
    const snapshotDimensions = dimensions ? dimensions : ThumbnailConstants.large;
    const { width, height } = snapshotDimensions;

    // get selected background
    const selectedBackground = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterBackground);

    let snapshotBackground;
    switch (imageType) {
      case ImageTypeEnum.PNG:
        snapshotBackground = selectedBackground ? selectedBackground : appDefaultBackground;
        break;
      case ImageTypeEnum.JPEG:
      default:
        snapshotBackground = selectedBackground && !BackgroundModel.isTransparent(selectedBackground) ? selectedBackground : appDefaultBackground;
        break;
    }

    // get logo based on background color
    const outerRGB = selectedBackground.colors.slice(0, 3) as [number, number, number];
    const isLight = ColorUtils.isLight(outerRGB);

    const snapshotConfig: SnapshotConfig = {
      ...defaultSnapshotConfig,
      crop: crop,
      imageType: imageType,
      imageQuality: imageQuality,
      copyrightEnabled: includeCopyright,
      copyrightImagePath: (isLight) ? 'assets/images/powered-by-biodigital-color.svg' : 'assets/images/powered-by-biodigital-white.svg'
    };

    setTimeout(() => {
      this.snapshotService.takeSnapshot(width, height, snapshotBackground, layers, includeLabels, snapshotConfig)
        .subscribe((blob) => {
          context.dispatch(new TakeChapterPictureSuccess(chapterId, blob, snapshotDimensions));
        });
    });
  }

  @Action(TakeChapterPictureSuccess)
  takeChapterPictureSuccess(context: StateContext<StoryChaptersStateModel>, action: TakeChapterPictureSuccess) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        const thumbnail =
          draft.thumbnails.get(action.chapterId) || new StoryChapterThumbnail();
        thumbnail.snapshot = URL.createObjectURL(action.blob);
        draft.thumbnails.set(action.chapterId, thumbnail);
      })
    );
  }

  @Action(RecordCamera)
  RecordCamera(context: StateContext<StoryChaptersStateModel>, action: RecordCamera) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        chapter.camera.location = action.camera;
      })
    );
  }

  @Action(SetChapterCurrentLayersIndexPosition)
  setChapterCurrentLayersIndexPosition(context: StateContext<StoryChaptersStateModel>, action: SetChapterCurrentLayersIndexPosition) {
    
    const [nextState, patches, inversePatches] = immer.produceWithPatches(context.getState(), draft => {
      const chapter = draft.chapters.find(c => c.id === action.chapterId);
      const currentLayers = chapter.visibilityLayers.current;
      currentLayers.index = action.index;
      currentLayers.position = action.position;
    });

    const finalState = immer.produce(nextState, draft => {
      draft.patches.push(...patches);
      draft.inversePatches.push(...inversePatches);
    });

    context.patchState(finalState);
    
    if (action.record) {
      context.dispatch(
        new UndoActions.SavePatches([{
          forward: finalState.patches,
          inverse: finalState.inversePatches,
          patchAction: ApplyChapterPatch
        }])
      );
    }
  }

  @Action(SetChapterMedia)
  setChapterMedia(context: StateContext<StoryChaptersStateModel>, action: SetChapterMedia) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        chapter.media = action.media;
      })
    );
  }

  @Action(SetChapterContentBlocks)
  setChapterContentBlocks(context: StateContext<StoryChaptersStateModel>, action: SetChapterContentBlocks) {
    // FIXME: Until we build content block re-ordering UI, manually reorder blocks so that ticker always comes first
    action.contentBlocks = [
      ...action.contentBlocks.filter(block => block.type === ContentBlockTypes.Ticker),
      ...action.contentBlocks.filter(block => block.type !== ContentBlockTypes.Ticker)
    ];

    context.patchState(
      immer.produce(context.getState(), draft => {
        const chapter = draft.chapters.find(c => c.id === action.chapterId);
        chapter.contentBlocks = action.contentBlocks;
      })
    );
  }

  constructor(
    private chapterService: ChapterService,
    private snapshotService: SnapshotService,
    private store: Store,
    private animationService: AnimationService,
    private engineService: EngineService
  ) { }
}
